import type { BonDruck } from '../bonweg';
import { aktuelleRechte } from '../bediener';
import { useCallback, useEffect, useState } from 'react';

import {
  NULL_BETRAEGE,
  type BelegAntwort,
  type Geraeteauftrag,
  type KassenApi,
  type KasseStatus,
} from '../api';
import { Belegansicht } from './Belegansicht';
import { sonderbelegnummer } from '../belegnummern';
import { formatiereEuro } from '../warenkorb';

/** Kassenstatus: Kettenende, Umsatzzähler, Ausfall, Pflichtbelege, Signaturkontingent. */
export function Status(props: {
  api: KassenApi;
  kassenId: string;
  bridgeDruck?: BonDruck;
  emailSenden?: (belegnummer: string, an: string, form?: 'bon' | 'rechnung') => Promise<string>;
}) {
  const { api, kassenId } = props;
  const [status, setStatus] = useState<KasseStatus | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [tagesabschluss, setTagesabschluss] = useState<Geraeteauftrag | null>(null);
  /** Zuletzt erstellter Null-/Monats-/Jahresbeleg – zum Ausdrucken und Aufbewahren (Jahresbeleg). */
  const [erstellt, setErstellt] = useState<{ beleg: BelegAntwort; text: string | null } | null>(
    null,
  );

  const laden = useCallback(async () => {
    try {
      setStatus(await api.status(kassenId));
      setFehler(null);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, kassenId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function ausfallUmschalten(): Promise<void> {
    if (status === null) {
      return;
    }
    setMeldung(null);
    try {
      if (status.ausfallAktiv) {
        await api.ausfallBeenden(kassenId, sonderbelegnummer('NULL').replace(/^N-/, 'SB-'));
        setMeldung('Ausfall beendet – Sammelbeleg signiert.');
      } else {
        const grund = window.prompt(
          'Grund des Ausfalls der Sicherheitseinrichtung?',
          'Signaturdienst nicht erreichbar',
        );
        if (grund === null || grund.trim() === '') {
          return;
        }
        await api.ausfallBeginnen(kassenId, 'Signaturdienst', grund.trim());
        setMeldung('Ausfall begonnen – Belege werden als Ausfallbelege erstellt.');
      }
      await laden();
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  /** Tagesabschluss am Terminal: Auftrag anlegen und bis zum Ergebnis der Bridge abfragen. */
  async function terminalTagesabschluss(): Promise<void> {
    setMeldung(null);
    setFehler(null);
    try {
      let a = await api.terminalTagesabschluss(kassenId);
      setTagesabschluss(a);
      const frist = Date.now() + 120_000;
      while ((a.status === 'offen' || a.status === 'uebernommen') && Date.now() < frist) {
        await new Promise((r) => setTimeout(r, 1_000));
        a = await api.terminalAuftrag(kassenId, a.auftragId);
        setTagesabschluss(a);
      }
      if (a.status === 'offen' || a.status === 'uebernommen') {
        setFehler('Tagesabschluss noch nicht abgeschlossen – läuft die Bridge im Laden?');
      }
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  async function ausserBetriebNehmen(): Promise<void> {
    if (
      !window.confirm(
        `Kasse ${kassenId} endgültig außer Betrieb nehmen? Es wird ein Schlussbeleg signiert; danach sind keine Belege mehr möglich. Der Schlussbeleg ist auszudrucken und aufzubewahren, die Außerbetriebnahme binnen einer Woche über FinanzOnline zu melden.`,
      )
    ) {
      return;
    }
    setMeldung(null);
    try {
      const b = await api.ausserBetriebNehmen(
        kassenId,
        sonderbelegnummer('NULL').replace(/^N-/, 'SCHLUSS-'),
      );
      setMeldung(
        `Schlussbeleg ${b.belegnummer} erstellt – Kasse ist außer Betrieb. Beleg unter „Belege" ausdrucken.`,
      );
      await laden();
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  const fehltMonat = status?.pflichtbelege.monatsbelegFehltFuer ?? null;
  const fehltJahr = status?.pflichtbelege.jahresbelegFehltFuer ?? null;

  /** Monats-/Jahresbeleg: fehlt einer, wird er mit der Nummer des versäumten Monats nachgeholt. */
  async function sonderbeleg(art: 'NULL' | 'MONATSBELEG' | 'JAHRESBELEG'): Promise<void> {
    setMeldung(null);
    let sonderart = art;
    let belegnummer = sonderbelegnummer('NULL');
    if (art === 'MONATSBELEG' && fehltMonat !== null) {
      if (fehltMonat.endsWith('-12')) {
        sonderart = 'JAHRESBELEG';
        belegnummer = `JB-${fehltMonat.slice(0, 4)}`;
      } else {
        belegnummer = `MB-${fehltMonat}`;
      }
    } else if (art === 'JAHRESBELEG' && fehltJahr !== null) {
      belegnummer = `JB-${fehltJahr}`;
    }
    try {
      const b = await api.erstelleBeleg(kassenId, {
        belegtyp: 'NULL',
        belegnummer,
        betraege: NULL_BETRAEGE,
        ...(sonderart === 'NULL' ? {} : { sonderbeleg: sonderart }),
      });
      setMeldung(`${sonderart === 'NULL' ? 'Nullbeleg' : sonderart} ${b.belegnummer} erstellt`);
      const text = await api.druck(kassenId, b.belegnummer).catch(() => null);
      setErstellt({ beleg: b, text });
      await laden();
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  if (status === null) {
    return <div className="karte">{fehler ?? 'Lade Status …'}</div>;
  }
  const s = status.signaturen;
  return (
    <>
      <div className="karte">
        <dl className="status">
          <dt>Kasse</dt>
          <dd>
            {status.kassenId} · {status.status ?? 'ohne Stammdaten'}
          </dd>
          <dt>Unternehmer</dt>
          <dd>
            {status.unternehmer === null
              ? '–'
              : `${status.unternehmer.name}, ${status.unternehmer.anschrift}${status.unternehmer.uid === undefined ? '' : ` (${status.unternehmer.uid})`}`}
          </dd>
          <dt>Zahlung</dt>
          <dd>
            {status.zahlungsanbieter ?? 'Standard'} · Terminal {status.zahlungsterminal ?? '–'}
            {status.bridgeZugelassen && ' · Bridge zugelassen'}
          </dd>
          <dt>Letzter Beleg</dt>
          <dd>
            {status.letzterBeleg === null
              ? 'noch kein Startbeleg'
              : `${status.letzterBeleg.belegnummer} (${status.letzterBeleg.belegtyp}${status.letzterBeleg.sonderbeleg === null ? '' : `, ${status.letzterBeleg.sonderbeleg}`}) · ${new Date(status.letzterBeleg.zeitpunkt).toLocaleString('de-AT')}`}
          </dd>
          <dt>Umsatzzähler</dt>
          <dd>
            {status.letzterBeleg === null
              ? '–'
              : `${formatiereEuro(status.letzterBeleg.umsatzzaehlerNachher)} €`}
          </dd>
          {status.schlussbeleg !== null && (
            <>
              <dt>Außer Betrieb</dt>
              <dd className="warnung">
                Schlussbeleg {status.schlussbeleg.belegnummer} vom{' '}
                {new Date(status.schlussbeleg.zeitpunkt).toLocaleString('de-AT')} – keine Belege
                mehr möglich; Außerbetriebnahme über FinanzOnline melden.
              </dd>
            </>
          )}
          <dt>Sicherheitseinrichtung</dt>
          <dd className={status.ausfallAktiv ? 'fehler' : 'ok'}>
            {status.ausfallAktiv
              ? 'AUSFALL aktiv – Belege werden als Ausfallbelege erstellt'
              : 'in Betrieb'}
          </dd>
          <dt>Pflichtbelege</dt>
          <dd>
            {status.pflichtbelege.monatsbelegFehltFuer === null &&
            status.pflichtbelege.jahresbelegFehltFuer === null ? (
              <span className="ok">vollständig</span>
            ) : (
              <span className="warnung">
                {status.pflichtbelege.monatsbelegFehltFuer !== null &&
                  `Monatsbeleg ${status.pflichtbelege.monatsbelegFehltFuer} fehlt `}
                {status.pflichtbelege.jahresbelegFehltFuer !== null &&
                  `Jahresbeleg ${status.pflichtbelege.jahresbelegFehltFuer} fehlt`}
              </span>
            )}
          </dd>
          <dt>Signaturen {s?.jahr ?? ''}</dt>
          <dd className={s?.stufe === 'ok' || s?.stufe === 'unbekannt' ? '' : 'warnung'}>
            {s === null
              ? 'nicht abrufbar'
              : `${String(s.verbraucht)}${s.limit === null ? '' : ` / ${String(s.limit)}`} (${s.produkt ?? 'Produkt unbekannt'}, ${s.stufe})`}
          </dd>
        </dl>
      </div>
      <div className="karte">
        {status.status === 'ausser_betrieb' && (
          <p className="warnung">Kasse ist außer Betrieb – keine weiteren Belege.</p>
        )}
        <div
          className="zeile"
          style={status.status === 'ausser_betrieb' ? { display: 'none' } : {}}
        >
          <button
            className="taste zweit"
            onClick={() => {
              void sonderbeleg('NULL');
            }}
          >
            Nullbeleg (Prüfung)
          </button>
          {/* Nur für abgeschlossene Monate – im laufenden Monat lehnt die API ab. */}
          {fehltMonat !== null && (
            <button
              className="taste zweit"
              onClick={() => {
                void sonderbeleg('MONATSBELEG');
              }}
            >
              {`${fehltMonat.endsWith('-12') ? 'Jahresbeleg' : 'Monatsbeleg'} ${fehltMonat} nachholen`}
            </button>
          )}
          {fehltJahr !== null && !fehltMonat?.endsWith('-12') && (
            <button
              className="taste zweit"
              onClick={() => {
                void sonderbeleg('JAHRESBELEG');
              }}
            >
              {`Jahresbeleg ${fehltJahr} nachholen`}
            </button>
          )}
          <button
            className="taste zweit"
            onClick={() => {
              void laden();
            }}
          >
            Aktualisieren
          </button>
          <button
            className={status.ausfallAktiv ? 'taste' : 'taste gefahr'}
            onClick={() => {
              void ausfallUmschalten();
            }}
          >
            {status.ausfallAktiv ? 'Ausfall beenden (Sammelbeleg)' : 'Ausfall beginnen'}
          </button>
          {status.zahlungsanbieter === 'zvt' && (
            <button
              className="taste zweit"
              disabled={
                !aktuelleRechte(sessionStorage, kassenId).tagesabschluss ||
                tagesabschluss?.status === 'offen' ||
                tagesabschluss?.status === 'uebernommen'
              }
              title={
                aktuelleRechte(sessionStorage, kassenId).tagesabschluss
                  ? undefined
                  : 'Kein Abschlussrecht für den angemeldeten Bediener'
              }
              onClick={() => {
                void terminalTagesabschluss();
              }}
            >
              Terminal-Tagesabschluss
            </button>
          )}
          {/* E-W1-RKSV F3 (M2-b): bei aktiver Bedienerverwaltung nur mit Abschlussrecht – der
              Server verweigert den Schlussbeleg sonst mit 403 */}
          <button
            className="taste gefahr"
            disabled={
              status.ausfallAktiv || !aktuelleRechte(sessionStorage, kassenId).tagesabschluss
            }
            title={
              aktuelleRechte(sessionStorage, kassenId).tagesabschluss
                ? 'Schlussbeleg signieren; danach keine Belege mehr'
                : 'Kein Abschlussrecht für den angemeldeten Bediener'
            }
            onClick={() => {
              void ausserBetriebNehmen();
            }}
          >
            Außer Betrieb nehmen (Schlussbeleg)
          </button>
        </div>
        {tagesabschluss !== null && (
          <p className={tagesabschluss.status === 'fehlgeschlagen' ? 'fehler' : 'hinweis'}>
            Terminal-Tagesabschluss {tagesabschluss.auftragId}: {tagesabschluss.status}
            {typeof tagesabschluss.details.ergebnis === 'object' &&
              tagesabschluss.details.ergebnis !== null &&
              ` – ${(tagesabschluss.details.ergebnis as { text?: string }).text ?? ''}${
                typeof (tagesabschluss.details.ergebnis as { betragCent?: number | null })
                  .betragCent === 'number'
                  ? `, Summe ${formatiereEuro((tagesabschluss.details.ergebnis as { betragCent: number }).betragCent)} €`
                  : ''
              }`}
          </p>
        )}
        {meldung !== null && <p className="ok">{meldung}</p>}
        {fehler !== null && <p className="fehler">{fehler}</p>}
        <p className="hinweis">
          Monats- und Jahresbelege erzeugt die API automatisch nach dem Monatsende, spätestens vor
          dem ersten Beleg des neuen Monats; fehlt einer, lässt er sich hier nachholen. Der
          Jahresbeleg ist auszudrucken, zu prüfen und aufzubewahren.
        </p>
      </div>
      {erstellt !== null && (
        <div className="karte">
          <h3>
            {erstellt.beleg.sonderbeleg === 'JAHRESBELEG'
              ? 'Jahresbeleg – ausdrucken, prüfen und aufbewahren'
              : `${erstellt.beleg.sonderbeleg ?? 'Nullbeleg'} ${erstellt.beleg.belegnummer}`}
          </h3>
          <Belegansicht
            beleg={erstellt.beleg}
            text={erstellt.text}
            {...(props.bridgeDruck === undefined ? {} : { bridgeDruck: props.bridgeDruck })}
            {...(props.emailSenden === undefined ? {} : { emailSenden: props.emailSenden })}
            aktionen={
              <button
                className="taste zweit"
                onClick={() => {
                  setErstellt(null);
                }}
              >
                Schließen
              </button>
            }
          />
        </div>
      )}
    </>
  );
}
